import { Component, signal, inject, OnInit, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';

import { UserRole } from '@core/interfaces/role.interface';
import { Auth } from '@core/services';
import { Package as PackageService } from '@modules/package/services/package';
import { DatePicker } from '@shared/date-picker/date-picker';
import { DataNotFound } from '@shared/data-not-found/data-not-found';
import { Loader } from '@shared/loader/loader';
import { Pagination } from '@shared/pagination/pagination';
import { Title, Toaster } from '@shared/services';

@Component({
  selector: 'app-package',
  imports: [CommonModule, Pagination, ReactiveFormsModule, DatePicker, DataNotFound, Loader],
  templateUrl: './package.html',
  styleUrl: './package.scss'
})
export class Package implements OnInit {
  private fb = inject(FormBuilder);
  private authService = inject(Auth);
  private titleService = inject(Title);
  private packageService = inject(PackageService);
  private toasterService = inject(Toaster);

  isLoading = signal(false);
  selectedFile = signal<File | null>(null);
  isUploading = signal(false);
  packages = signal<any[]>([]);
  totalItems = signal(0);
  pageSize = signal(5);
  currentPage = signal(1);
  isModalOpen = signal(false);
  isDeleteModalOpen = signal(false);
  isEditMode = signal(false);
  packageToDelete = signal<any>(null);
  packageForm: FormGroup = this.fb.group({
    name: ['', [Validators.required]],
    price: ['', [Validators.required]],
    startDate: ['', [Validators.required]],
    expiryDate: ['', [Validators.required]],
    packageType: ['', [Validators.required]]
  });

  user = computed(() => this.authService.user || {});
  isAdmin = computed(() => this.user().role?.name === UserRole.ADMIN);

  ngOnInit() {
    this.titleService.setTitle('Package');
    this.getPackages();
  }

  getPackages() {
    this.isLoading.set(true);
    this.packages.set([]);

    const queryParams = {
      page: this.currentPage(),
      pageSize: this.pageSize()
    };

    this.packageService.getPackages(queryParams).subscribe({
      next: (response) => {
        this.packages.set(response.data);
        this.totalItems.set(response.pagination.total);
        this.isLoading.set(false);
      },
      error: (error) => {
        this.toasterService.errorAlert(error.error.message);
        this.isLoading.set(false);
      }
    });
  }

  onFileSelected(event: any) {
    const file = event.target.files?.[0];
    if (file) {
      this.selectedFile.set(file);
    }
  }

  uploadFile() {
    if (!this.selectedFile()) {
      this.toasterService.errorAlert('Please select a file');
      return;
    }

    this.isUploading.set(true);
    const formData = new FormData();
    formData.append('file', this.selectedFile() as File);

    this.packageService.uploadPackages(formData).subscribe({
      next: () => {
        this.toasterService.successAlert('Packages uploaded successfully');
        this.selectedFile.set(null);
        this.isUploading.set(false);
        this.getPackages();
      },
      error: (error) => {
        this.toasterService.errorAlert(error.error?.message || 'Failed to upload packages');
        this.isUploading.set(false);
      }
    });
  }

  onPageChange(page: number) {
    this.currentPage.set(page);
    this.getPackages();
  }

  onPageSizeChange(size: number) {
    this.pageSize.set(size);
    this.currentPage.set(1);
    this.getPackages();
  }

  savePackage() {
    if (this.packageForm.invalid) {
      this.toasterService.errorAlert('Please fill in all required fields correctly');
      return;
    }

    const data = this.packageForm.getRawValue();
    const packageData: any = {
      name: data.name,
      price: data.price,
      startDate: data.startDate,
      expiryDate: data.expiryDate,
      packageType: data.packageType
    };

    if (this.isEditMode()) {
      const existingData = this.packages().find((pkg: any) => pkg.name === data.name);
      if (!existingData) {
        this.toasterService.errorAlert('No package found for update');
        return;
      }

      this.packageService.editPackage(existingData._id, packageData).subscribe({
        next: () => {
          this.toasterService.successAlert('Package updated successfully!');
          this.getPackages();
        },
        error: (error) => {
          this.toasterService.errorAlert(error.error.message);
        }
      });
    } else {
      this.packageService.addPackage(packageData).subscribe({
        next: () => {
          this.toasterService.successAlert('Package added successfully!');
          this.getPackages();
        },
        error: (error) => {
          this.toasterService.errorAlert(error.error.message);
        }
      });
    }

    this.closeModal();
  }

  closeModal() {
    this.isModalOpen.set(false);
    this.isEditMode.set(false);
    this.packageForm.reset();
  }

  onAddPackage() {
    this.isEditMode.set(false);
    this.packageForm.reset();
    this.isModalOpen.set(true);
  }

  onEdit(pkg: any) {
    const existingData = this.packages().find((pack: any) => pack._id === pkg._id);

    if (!existingData) {
      this.toasterService.errorAlert('No package found for update');
      return;
    }

    this.isEditMode.set(true);
    this.packageForm.patchValue({
      name: existingData.name,
      price: existingData.price,
      startDate: existingData.startDate,
      expiryDate: existingData.expiryDate,
      packageType: existingData.packageType
    });
    this.isModalOpen.set(true);
  }

  onDelete(pkg: any) {
    this.packageToDelete.set(pkg);
    this.isDeleteModalOpen.set(true);
  }

  confirmDelete() {
    const pkg = this.packageToDelete();
    if (!pkg) {
      this.toasterService.errorAlert('No package found for delete');
      return;
    }

    this.packageService.deletePackage(pkg._id).subscribe({
      next: () => {
        this.toasterService.successAlert('Package deleted successfully!');
        this.getPackages();
        this.closeDeleteModal();
      },
      error: (error) => {
        this.toasterService.errorAlert(error.error.message || 'Failed to delete package');
      }
    });
  }

  closeDeleteModal() {
    this.isDeleteModalOpen.set(false);
    this.packageToDelete.set(null);
  }
}
